import { setMetricObjects } from './builder'
import { DEFAULT_FRAME, FRAMES, frameOf } from './frames'
import { AggFn, FrameId } from './pipeline-types'
import { Filter, Metric, NumericField, ViewConfig } from './view-config'

function measureFor(frame: FrameId, agg: AggFn, field: string | null): Metric {
  if (agg === 'cumsum') return measureFor(frame, 'sum', field)
  if (agg === 'cumcount') return measureFor(frame, 'count', null)
  const measures = FRAMES[frame].measures
  if (agg === 'distinct') {
    const label = FRAMES[frame].labels[field ?? ''] ?? field ?? ''
    return {
      agg: 'distinct',
      field: field ?? undefined,
      label: `Distinct ${label}`,
    }
  }
  if (agg === 'count') {
    return (
      measures.find((m) => m.agg === 'count') ?? {
        agg: 'count',
        label: 'Count',
      }
    )
  }
  const named = measures.find((m) => m.agg === agg && m.field === field)
  if (named) return named
  const label = FRAMES[frame].labels[field ?? ''] ?? field ?? ''
  return {
    agg,
    field: field ?? undefined,
    label: agg === 'avg' ? `Avg ${label}` : label,
  }
}

/**
 * Pure transforms behind the report builder's sidebar controls. Each control
 * (Show as, Break down by, Measure, Top N, the filter rows) maps to one
 * ViewConfig mutation here, so the engine / saved-views / view-switcher all
 * keep consuming a plain ViewConfig.
 *
 * Shape derives from the config: groupBy → grouped aggregate (chart/summary);
 * metrics with no groupBy → metric (one value); otherwise → a raw list of rows.
 *
 * Field roles (which columns can be grouped, measured, filtered, sorted) come
 * from the config's frame (see frames.ts), so the same controls drive any frame.
 */

/** Categorical fields the frame offers as `in` filters / breakdowns. */
export const categoricalFields = (c: ViewConfig): string[] =>
  frameOf(c.frame).catDims

/** Fields a list frame can sort by (its full column set). */
export const sortableFields = (c: ViewConfig): string[] =>
  frameOf(c.frame).columns

/** Numeric fields the frame offers as threshold filters / measures. */
export const numericFields = (c: ViewConfig): string[] =>
  frameOf(c.frame).numFields

/** Date fields the frame offers as recency filters / time buckets. */
export const dateFields = (c: ViewConfig): string[] =>
  frameOf(c.frame).dateFields

// ── Filter ────────────────────────────────────────────────────────────────
/**
 * Replace the first filter matching `match` in place (preserving its position),
 * or append `next` when none matches. Editing a filter must NOT reorder it —
 * the quick-filter bar renders pills in `filters` order, so a remove-then-append
 * would make an edited pill jump to the end.
 */
const replaceOrAppend = (
  filters: Filter[],
  next: Filter,
  match: (f: Filter) => boolean
): Filter[] => {
  const idx = filters.findIndex(match)
  if (idx === -1) return [...filters, next]
  return filters.map((f, i) => (i === idx ? next : f))
}

export const inFilterFields = (c: ViewConfig): string[] =>
  c.filters.flatMap((f) => (f.op === 'in' ? [f.field] : []))

export const inFilterValues = (c: ViewConfig, field: string): string[] => {
  const f = c.filters.find((x) => x.op === 'in' && x.field === field)
  return f && f.op === 'in' ? f.value : []
}

export const setInFilter = (
  c: ViewConfig,
  field: string,
  values: string[]
): ViewConfig => {
  // Keep the filter even when nothing is selected (an empty `in` is Any — the
  // engine treats it as no constraint) so deselecting all leaves the row in
  // place. Removing the row entirely is the explicit Remove action.
  const next: Filter = { field, op: 'in', value: values, fromKnob: true }
  return {
    ...c,
    filters: replaceOrAppend(
      c.filters,
      next,
      (f) => f.op === 'in' && f.field === field
    ),
  }
}

export const removeInFilter = (c: ViewConfig, field: string): ViewConfig => ({
  ...c,
  filters: c.filters.filter((f) => !(f.op === 'in' && f.field === field)),
})

// ── Numeric filters (gte / lte) ─────────────────────────────────────────────
type NumericCond = { field: NumericField; op: 'gte' | 'lte'; value: number }

/** One numeric threshold per field (v1): the current condition, if any. */
export const numericFilterOf = (
  c: ViewConfig,
  field: NumericField
): NumericCond | undefined =>
  c.filters.find(
    (f): f is NumericCond =>
      (f.op === 'gte' || f.op === 'lte') && f.field === field
  )

export const numericFilterFields = (c: ViewConfig): NumericField[] => {
  const seen: NumericField[] = []
  for (const f of c.filters) {
    if ((f.op === 'gte' || f.op === 'lte') && !seen.includes(f.field)) {
      seen.push(f.field)
    }
  }
  return seen
}

/** Set the (single) threshold for a field, replacing any existing one. */
export const setNumericFilter = (
  c: ViewConfig,
  field: NumericField,
  op: 'gte' | 'lte',
  value: number
): ViewConfig => ({
  ...c,
  filters: replaceOrAppend(
    c.filters,
    { field, op, value },
    (f) => (f.op === 'gte' || f.op === 'lte') && f.field === field
  ),
})

export const removeNumericFilter = (
  c: ViewConfig,
  field: NumericField
): ViewConfig => ({
  ...c,
  filters: c.filters.filter(
    (f) => !((f.op === 'gte' || f.op === 'lte') && f.field === field)
  ),
})

// ── Date filters (absolute bound or rolling recency window) ──────────────────
/**
 * A date field bounded one of two ways, both surfaced in the same filter row:
 *   - absolute: `date_after` (on or after) / `date_before` (on or before) + a
 *     yyyy-mm-dd value — the date analogue of the numeric gte/lte threshold.
 *   - rolling: `within_days` + a day count, e.g. "closed in the last 90 days".
 * One bound per field (v1).
 */
export type DateCond =
  | { field: string; op: 'date_after' | 'date_before'; value: string }
  | { field: string; op: 'within_days'; value: number }

export type DateOp = DateCond['op']

const isDateCompare = (f: Filter): f is DateCond =>
  f.op === 'date_after' || f.op === 'date_before' || f.op === 'within_days'

export const dateFilterFields = (c: ViewConfig): string[] => {
  const seen: string[] = []
  for (const f of c.filters) {
    if (isDateCompare(f) && !seen.includes(f.field)) seen.push(f.field)
  }
  return seen
}

export const dateFilterOf = (
  c: ViewConfig,
  field: string
): DateCond | undefined =>
  c.filters.find((f): f is DateCond => isDateCompare(f) && f.field === field)

/** Set the (single) date bound for a field, replacing any existing one. */
export const setDateFilter = (
  c: ViewConfig,
  field: string,
  op: DateOp,
  value: string | number
): ViewConfig => ({
  ...c,
  filters: replaceOrAppend(
    c.filters,
    op === 'within_days'
      ? { field, op, value: Number(value) || 0 }
      : { field, op, value: String(value) },
    (f) => isDateCompare(f) && f.field === field
  ),
})

export const removeDateFilter = (c: ViewConfig, field: string): ViewConfig => ({
  ...c,
  filters: c.filters.filter((f) => !(isDateCompare(f) && f.field === field)),
})

// ── Open-deals filter (is_open) ─────────────────────────────────────────────
export const hasOpenFilter = (c: ViewConfig): boolean =>
  c.filters.some((f) => f.op === 'is_open')

export const setOpenFilter = (c: ViewConfig, on: boolean): ViewConfig => ({
  ...c,
  filters: on
    ? hasOpenFilter(c)
      ? c.filters
      : [...c.filters, { op: 'is_open' }]
    : c.filters.filter((f) => f.op !== 'is_open'),
})

// ── Group by ────────────────────────────────────────────────────────────────
/**
 * Break a summary down by `field` (a category, or a date field with a
 * month/quarter `bucket`) — turning a single metric into a grouped aggregate.
 * Keeps the measure and clears any series; presentation (`chart`) is left
 * untouched, since shape and drawing are independent (the view switcher owns
 * `chart`).
 */
export const applyGroupBy = (
  c: ViewConfig,
  field: string,
  bucket?: 'month' | 'quarter'
): ViewConfig => {
  const measures = frameOf(c.frame).measures
  return {
    ...c,
    kind: 'aggregate',
    groupBy: { field, bucket },
    seriesBy: undefined,
    // Running total only applies over a time bucket — drop a stale cumulative
    // flag when the breakdown becomes categorical.
    cumulative: bucket ? c.cumulative : undefined,
    metrics: c.metrics?.length ? c.metrics : [measures[0]],
    chartMetric: c.chartMetric ?? c.metrics?.[0]?.label ?? measures[0].label,
    // Chart (how it's drawn) is preserved — changing the breakdown reshapes the
    // data, not the presentation. The view switcher owns `chart`.
    columns: undefined,
    sort: undefined,
    limit: undefined,
  }
}

// ── Aggregate (measures) ────────────────────────────────────────────────────
/** The current primary metric (drives the agg/field controls). */
const primaryMetric = (c: ViewConfig) =>
  c.metrics?.[0] ?? frameOf(c.frame).measures[0]

/**
 * Set the primary measure as an explicit aggregation + field pair — Count needs
 * no field; Total/Average operate on a numeric field. Resolves to the frame's
 * named measure when one matches (so the label/legend stays curated, e.g.
 * "Yearly Revenue" or "Win Rate"), else a generated label. Keeps any compare
 * measure and the current breakdown/presentation, so swapping the aggregation
 * never silently reshapes the report.
 */
export const setPrimaryAgg = (
  c: ViewConfig,
  agg: AggFn,
  field: string | null
): ViewConfig => {
  const primary = measureFor(
    c.frame ?? DEFAULT_FRAME,
    agg,
    agg === 'count' ? null : field
  )
  const second = c.metrics?.[1]
  const keepSecond =
    second && second.label !== primary.label ? second : undefined
  const metrics = keepSecond ? [primary, keepSecond] : [primary]
  const base = setMetricObjects({ ...c, kind: 'aggregate' }, metrics)
  return c.groupBy
    ? {
        ...base,
        chartMetric2: keepSecond?.label,
        chart: c.chart === 'table-only' ? 'bar' : c.chart,
      }
    : {
        ...base,
        chartMetric2: undefined,
        groupBy: undefined,
        seriesBy: undefined,
        chart: 'table-only',
        columns: undefined,
        sort: undefined,
        limit: undefined,
      }
}

/**
 * Set (or clear, with null) the compare measure for a dual-axis combo. Adds the
 * chosen named measure to `metrics` so the engine computes it; clearing
 * collapses back to the primary alone. Preserves the (possibly synthesized)
 * primary measure object.
 */
export const setCompareMeasure = (
  c: ViewConfig,
  label: string | null
): ViewConfig => {
  const primary = primaryMetric(c)
  if (!label || label === primary.label) {
    return { ...setMetricObjects(c, [primary]), chartMetric2: undefined }
  }
  const second = frameOf(c.frame).measures.find((m) => m.label === label)
  if (!second) return c
  return {
    ...setMetricObjects(c, [primary, second]),
    chartMetric2: label,
    // A dual-measure combo and a series split can't coexist (the engine pivots
    // one or the other), so adding a second measure clears any series.
    seriesBy: undefined,
  }
}

/**
 * Split a grouped report into per-series values — one stacked/grouped bar
 * segment (or, over a time bucket, one line) per distinct value of `field`.
 * Clears any dual-measure combo, which can't coexist with a series.
 */
export const setSeriesBy = (
  c: ViewConfig,
  field: string | null
): ViewConfig => ({
  ...c,
  seriesBy: field ?? undefined,
  chartMetric2: field ? undefined : c.chartMetric2,
})

// ── Sort / Limit / Columns (list frames) ────────────────────────────────────
export const setSort = (
  c: ViewConfig,
  by: string,
  dir: 'asc' | 'desc'
): ViewConfig => ({ ...c, sort: { by, dir } })

/**
 * Toggle the running-total (cumulative) flag. Sugar over the engine's
 * `cumulative` — the agg picker's "Running total" choice is (sum metric + this
 * flag). Only fires over a time bucket; the engine guards the rest, and we store
 * `undefined` rather than `false` to keep saved configs clean.
 */
export const setCumulative = (c: ViewConfig, on: boolean): ViewConfig => ({
  ...c,
  cumulative: on || undefined,
})

/** Top-N for a grouped chart/summary: keep the leading N groups (0 = all). */
export const setTopN = (c: ViewConfig, n: number): ViewConfig => ({
  ...c,
  limit: n > 0 ? n : undefined,
})

export const setColumns = (c: ViewConfig, columns: string[]): ViewConfig => ({
  ...c,
  columns,
})

// ── Shape predicate ─────────────────────────────────────────────────────────
/**
 * A grouped aggregate is a *time-series* iff its breakdown is a date bucket
 * (month/quarter) — the only shape with an evenly-spaced, ordered x-axis. Gates
 * which views the switcher offers (line only here; ranked only for categorical
 * breakdowns); see ViewSwitcher.availableViews.
 */
export const isTimeSeries = (c: ViewConfig): boolean => !!c.groupBy?.bucket
